import { useMemo } from 'react'
import { Box, Chip } from '@mui/material'
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium'
import { Button } from 'Common/Supplyvue-ui'
import { Panel } from 'Common/Components/Panel'
import { RunStatusChip } from 'Common/Components/RunStatusChip'
import { Table } from 'Common/MUITableV2'
import { methodLabel } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import {
  MethodComparison,
  TemplateSummaryOutputs,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { templateSummaryMetrics } from 'Features/Projects/Scenarios/Templates/TemplateResults/Results/Results.PlanTemplateSummary.view'
import { MethodSequence } from 'Features/Projects/Scenarios/Templates/TemplateResults/Comparison/MethodSequence'
import { MethodCycleSpeed } from 'Features/Projects/Scenarios/Templates/TemplateResults/Comparison/MethodCycleSpeed'

type MethodComparisonViewProps = {
  data: MethodComparison
  handleUseMethod: (method: string) => void
  isSaving: boolean
}

const CELL_SX = {
  px: '12px',
  py: '8px',
  borderBottom: '1px solid var(--line-100)',
  fontSize: 12,
  textAlign: 'right',
  whiteSpace: 'nowrap',
} as const

const HEAD_SX = {
  ...CELL_SX,
  background: 'var(--panel-head)',
  borderBottom: '1px solid var(--line-300)',
  verticalAlign: 'top',
} as const

// -1 is the snapshot's "no current plan" marker.
const planValue = (value: unknown) =>
  value === -1 || value === null || value === undefined ? null : value

/**
 * Every method's template for one work centre, side by side, top to bottom:
 * the summary figures as a metric × method table, with the choice of
 * preferred method under each column; each method's sequence of events on
 * one scale; the cycle split as one grouped chart; then each SKU group's
 * cycle time and start week per method next to the current plan.
 */
export const MethodComparisonView = ({
  data,
  handleUseMethod,
  isSaving,
}: MethodComparisonViewProps) => {
  const { methods, skuGroups, workCentre } = data
  const preferred = workCentre.preferredTemplate ?? 'constrained'

  // Labels and order from the first method with figures; every method's
  // values for the same keys.
  const metricRows = useMemo(() => {
    const sample = methods.find((m) => m.summary)?.summary
    if (!sample) return []
    return templateSummaryMetrics(sample as TemplateSummaryOutputs).map(
      (metric) => ({
        ...metric,
        values: methods.map((m) => ({
          method: m.templateMethod,
          value: m.summary
            ? templateSummaryMetrics(m.summary as TemplateSummaryOutputs).find(
                (item) => item.key === metric.key
              )?.value
            : undefined,
        })),
      })
    )
  }, [methods])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'skuGroupCode',
        headerName: 'SKU group',
        minWidth: 140,
        flex: 1,
        cellClassName: 'svp-mono',
      },
      {
        field: 'skuGroupName',
        headerName: 'Name',
        minWidth: 200,
        flex: 1.5,
      },
      ...(['currentCycleTime', 'currentStartWeek'] as const).map(
        (field): GridColDef => ({
          field,
          headerName: field === 'currentCycleTime' ? 'Cycle' : 'Start wk',
          type: 'number',
          width: 84,
          cellClassName: 'svp-num',
          valueGetter: (value) => planValue(value),
        })
      ),
      ...methods.flatMap(({ templateMethod }) =>
        (['cycleTime', 'startWeek'] as const).map(
          (field): GridColDef => ({
            field: `${field}_${templateMethod}`,
            headerName: field === 'cycleTime' ? 'Cycle' : 'Start wk',
            type: 'number',
            width: 84,
            cellClassName: 'svp-num',
          })
        )
      ),
    ],
    [methods]
  )

  const columnGroups: GridColumnGroupingModel = useMemo(
    () => [
      {
        groupId: 'current',
        headerName: 'Current plan',
        headerAlign: 'center',
        children: [
          { field: 'currentCycleTime' },
          { field: 'currentStartWeek' },
        ],
      },
      ...methods.map(({ templateMethod }) => ({
        groupId: templateMethod,
        headerName:
          methodLabel(templateMethod) +
          (templateMethod === preferred ? ' (preferred)' : ''),
        headerAlign: 'center' as const,
        children: [
          { field: `cycleTime_${templateMethod}` },
          { field: `startWeek_${templateMethod}` },
        ],
      })),
    ],
    [methods, preferred]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
      }}
    >
      <Panel title="Summary by method" bodyPadding="0">
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              color: 'var(--ink-900)',
            }}
          >
            <thead>
              <tr>
                <Box
                  component="th"
                  scope="col"
                  sx={{ ...HEAD_SX, textAlign: 'left', width: '34%' }}
                >
                  <Box
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: 'var(--ink-500)',
                    }}
                  >
                    Metric
                  </Box>
                </Box>
                {methods.map(({ templateMethod, status }) => (
                  <Box
                    component="th"
                    scope="col"
                    key={templateMethod}
                    sx={HEAD_SX}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '4px',
                      }}
                    >
                      <Box sx={{ fontSize: 13, fontWeight: 600 }}>
                        {methodLabel(templateMethod)}
                      </Box>
                      {templateMethod === preferred ? (
                        <Chip label="Preferred" color="primary" />
                      ) : (
                        status !== 'completed' && (
                          <RunStatusChip status={status} />
                        )
                      )}
                    </Box>
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {metricRows.length === 0 ? (
                <tr>
                  <Box
                    component="td"
                    colSpan={methods.length + 1}
                    sx={{
                      ...CELL_SX,
                      textAlign: 'left',
                      color: 'var(--ink-500)',
                    }}
                  >
                    No method has finished yet.
                  </Box>
                </tr>
              ) : (
                metricRows.map((metric) => (
                  <tr key={metric.key}>
                    <Box
                      component="th"
                      scope="row"
                      sx={{
                        ...CELL_SX,
                        textAlign: 'left',
                        fontWeight: 400,
                        color: 'var(--ink-600)',
                      }}
                    >
                      {metric.label}
                      {metric.unit && (
                        <Box
                          component="span"
                          sx={{ ml: '4px', color: 'var(--ink-400)' }}
                        >
                          ({metric.unit})
                        </Box>
                      )}
                    </Box>
                    {metric.values.map(({ method, value }) => (
                      <Box
                        component="td"
                        key={method}
                        sx={{
                          ...CELL_SX,
                          fontFamily: 'var(--font-mono)',
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 600,
                          color:
                            value === undefined
                              ? 'var(--ink-300)'
                              : 'var(--ink-900)',
                          ...(method === preferred && {
                            background: 'var(--teal-tint)',
                          }),
                        }}
                      >
                        {value ?? '—'}
                      </Box>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <Box component="td" sx={{ ...CELL_SX, borderBottom: 'none' }} />
                {methods.map(({ templateMethod, status, summary }) => (
                  <Box
                    component="td"
                    key={templateMethod}
                    sx={{ ...CELL_SX, borderBottom: 'none' }}
                  >
                    {templateMethod !== preferred && (
                      <Button
                        size="small"
                        disabled={
                          status !== 'completed' || !summary || isSaving
                        }
                        onClick={() => handleUseMethod(templateMethod)}
                      >
                        Use this method
                      </Button>
                    )}
                  </Box>
                ))}
              </tr>
            </tfoot>
          </Box>
        </Box>
      </Panel>

      <MethodSequence methods={methods} preferred={preferred} />

      <MethodCycleSpeed
        methods={methods.flatMap((m) =>
          m.status === 'completed' && m.summary
            ? [{ templateMethod: m.templateMethod, summary: m.summary }]
            : []
        )}
      />

      <Panel title="Cycle time and start week by SKU group" bodyPadding="0">
        <Box sx={{ height: 480, minWidth: 0 }}>
          <Table
            rows={skuGroups}
            columns={columns}
            columnGroupingModel={columnGroups}
            showToolbar
            pagination
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            getRowClassName={({ indexRelativeToCurrentPage }) =>
              indexRelativeToCurrentPage % 2 === 0 ? 'row--even' : 'row--odd'
            }
            sx={{ border: 'none', height: '100%' }}
          />
        </Box>
      </Panel>
    </Box>
  )
}
